import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { ApiResponse, PagedResult } from '../models/api.models';
import {
  ApprovalQuery,
  ApprovalRequest,
  ConfirmPaymentResponse,
  DDTransaction,
  DirectDebitMandate,
  InitiatePaymentResponse,
  PaymentsOverview,
  TransactionQuery,
} from '../models/finance.models';

const PAYMENTS_API = `${environment.baseAPI}/admin/payments`;
const DIRECT_DEBIT_API = `${environment.baseAPI}/DirectDebit`;

/**
 * Finance area (approver) API: one method per endpoint, returning the raw
 * response. No mapping or business rules; that's FinancePaymentsDataAccess.
 */
@Injectable({ providedIn: 'root' })
export class FinancePaymentsApi {
  private http = inject(HttpClient);

  /** GET /DirectDebit — the organisation's linked account (404 when none). */
  getMandate(): Observable<ApiResponse<DirectDebitMandate>> {
    return this.http.get<ApiResponse<DirectDebitMandate>>(DIRECT_DEBIT_API);
  }

  /**
   * POST /DirectDebit/refresh-status — latest status from SecurePay, returns the
   * organisation's account. 404 = no account; 502 = SecurePay unavailable.
   */
  refreshMandateStatus(): Observable<ApiResponse<DirectDebitMandate>> {
    return this.http.post<ApiResponse<DirectDebitMandate>>(`${DIRECT_DEBIT_API}/refresh-status`, {});
  }

  /** GET /admin/payments/direct-debit/overview */
  getOverview(range: { fromDate?: string; toDate?: string } = {}): Observable<ApiResponse<PaymentsOverview>> {
    return this.http.get<ApiResponse<PaymentsOverview>>(`${PAYMENTS_API}/direct-debit/overview`, { params: toParams(range) });
  }

  /** GET /admin/payments/pending-approval — requests waiting for the finance officer. */
  getPendingApproval(query: ApprovalQuery): Observable<ApiResponse<PagedResult<ApprovalRequest>>> {
    return this.http.get<ApiResponse<PagedResult<ApprovalRequest>>>(`${PAYMENTS_API}/pending-approval`, {
      params: toParams(query),
    });
  }

  /** GET /admin/payments/pending-approval/export */
  exportPendingApproval(format: 'csv' | 'pdf', query: ApprovalQuery): Observable<Blob> {
    return this.http.get(`${PAYMENTS_API}/pending-approval/export`, {
      params: toParams({ format, ...query }),
      responseType: 'blob',
    });
  }

  /** POST /admin/payments/{id}/reject */
  reject(payoutId: string, reason: string): Observable<unknown> {
    return this.http.post(`${PAYMENTS_API}/${payoutId}/reject`, { reason });
  }

  /** POST /admin/payments/{id}/pay/initiate — fee + OTP email. */
  initiatePayment(payoutId: string): Observable<ApiResponse<InitiatePaymentResponse>> {
    return this.http.post<ApiResponse<InitiatePaymentResponse>>(`${PAYMENTS_API}/${payoutId}/pay/initiate`, {});
  }

  /** POST /admin/payments/{id}/pay/confirm — full response, so 200 and 202 can be told apart. */
  confirmPayment(payoutId: string, otp: string): Observable<HttpResponse<ApiResponse<ConfirmPaymentResponse>>> {
    return this.http.post<ApiResponse<ConfirmPaymentResponse>>(
      `${PAYMENTS_API}/${payoutId}/pay/confirm`,
      { otp },
      { observe: 'response' },
    );
  }

  /** GET /admin/payments/direct-debit-transactions — Transaction History. */
  getTransactions(query: TransactionQuery): Observable<ApiResponse<PagedResult<DDTransaction>>> {
    return this.http.get<ApiResponse<PagedResult<DDTransaction>>>(`${PAYMENTS_API}/direct-debit-transactions`, {
      params: toParams(query),
    });
  }

  /** GET /admin/payments/direct-debit-transactions/export */
  exportTransactions(format: 'csv' | 'pdf', status?: string): Observable<Blob> {
    return this.http.get(`${PAYMENTS_API}/direct-debit-transactions/export`, {
      params: toParams({ format, status }),
      responseType: 'blob',
    });
  }
}

/** Builds query params, skipping empty values. */
function toParams(values: object): HttpParams {
  let params = new HttpParams();
  for (const [key, value] of Object.entries(values) as [string, string | number | null | undefined][]) {
    if (value !== undefined && value !== null && value !== '') params = params.set(key, value);
  }
  return params;
}
